/**
 * BatchResultDetailPage Component
 *
 * Displays aggregated results and statistics for a batch including:
 * - Summary statistics (total jobs, completed, failed, success rate)
 * - Evaluation metrics (if groundtruth columns configured)
 * - Quick filter tabs (All/Completed/Failed)
 * - Job list filtered by batch_id with evaluation status
 * - Export functionality
 *
 * Route: /batches/:batch_id/results
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBatch } from '../hooks/useBatches';
import { useJobs } from '../hooks/useJobs';
import { JobStatus } from '../types/job';
import { EvaluationMetrics } from '../types/evaluation';
import { EvaluationStatusBadge } from '../components/evaluation/EvaluationStatusBadge';
import { EvaluationMetricsCard } from '../components/evaluation/EvaluationMetricsCard';
import { evaluationService } from '../services/evaluationApi';
import toast from 'react-hot-toast';

type FilterTab = 'all' | 'completed' | 'failed';

export const BatchResultDetailPage: React.FC = () => {
  const { batch_id } = useParams<{ batch_id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [evaluationMetrics, setEvaluationMetrics] = useState<EvaluationMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Fetch batch data for statistics
  const { data: batch, isLoading: batchLoading, error: batchError } = useBatch(batch_id);

  // Fetch jobs filtered by batch and tab
  const jobStatus: JobStatus | undefined =
    activeTab === 'completed' ? 'completed' :
    activeTab === 'failed' ? 'run_failed' :
    undefined;

  const { data: jobsData, isLoading: jobsLoading } = useJobs({
    batch_id: batch_id,
    status: jobStatus,
    sort_by: 'created_at',
    sort_order: 'desc',
  });

  // Fetch evaluation metrics if batch has groundtruth columns
  useEffect(() => {
    const fetchMetrics = async () => {
      if (batch && batch.groundtruth_columns && batch.groundtruth_columns.length > 0 && batch_id) {
        setMetricsLoading(true);
        try {
          const metrics = await evaluationService.getEvaluationMetrics(batch_id);
          setEvaluationMetrics(metrics);
        } catch (error) {
          console.error('Failed to fetch evaluation metrics:', error);
        } finally {
          setMetricsLoading(false);
        }
      }
    };

    fetchMetrics();
  }, [batch, batch_id]);

  if (batchLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <svg
          className="animate-spin h-8 w-8 text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (batchError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading batch
                </h3>
                <p className="mt-1 text-sm text-red-700">{batchError.message}</p>
                <button
                  onClick={() => navigate(-1)}
                  className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Go back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!batch) {
    return null;
  }

  const hasEvaluation = batch.groundtruth_columns && batch.groundtruth_columns.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Go back"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-gray-900">{batch.name}</h1>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    batch.status === 'completed' ? 'bg-green-100 text-green-800' :
                    batch.status === 'running' ? 'bg-blue-100 text-blue-800' :
                    batch.status === 'failed' ? 'bg-red-100 text-red-800' :
                    batch.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {batch.status}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Batch Results
                </p>
              </div>
            </div>
            <button
              onClick={() => toast('Export feature will be available after Task Group 9')}
              className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Export Results
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Summary Statistics Cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Jobs */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900">{batch.total_jobs}</p>
              </div>
            </div>
          </div>

          {/* Completed Jobs */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">{batch.completed_jobs}</p>
              </div>
            </div>
          </div>

          {/* Failed Jobs */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Failed</p>
                <p className="text-2xl font-bold text-red-600">{batch.failed_jobs}</p>
              </div>
            </div>
          </div>

          {/* Success Rate */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Success Rate</p>
                <p className="text-2xl font-bold text-gray-900">{(batch.success_rate * 100).toFixed(1)}%</p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${batch.success_rate * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Evaluation Metrics Card */}
        {hasEvaluation && !metricsLoading && evaluationMetrics && (
          <EvaluationMetricsCard
            totalJobs={batch.total_jobs}
            passCount={evaluationMetrics.pass_count}
            failCount={evaluationMetrics.fail_count}
            errorCount={evaluationMetrics.error_count}
            naCount={evaluationMetrics.na_count}
            passRate={evaluationMetrics.pass_rate}
            commonFailureReasons={evaluationMetrics.common_failure_reasons}
          />
        )}

        {/* Quick Filter Tabs */}
        <div className="bg-white border border-gray-200 rounded-lg p-1 inline-flex">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'all'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Show All ({batch.total_jobs})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'completed'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Show Completed ({batch.completed_jobs})
          </button>
          <button
            onClick={() => setActiveTab('failed')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'failed'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Show Failed ({batch.failed_jobs})
          </button>
        </div>

        {/* Jobs List */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Jobs</h2>

          {jobsLoading && (
            <div className="flex justify-center py-8">
              <svg
                className="animate-spin h-6 w-6 text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}

          {!jobsLoading && jobsData && jobsData.jobs.length === 0 && (
            <p className="text-center text-gray-500 py-8">No jobs found for this filter.</p>
          )}

          {!jobsLoading && jobsData && jobsData.jobs.length > 0 && (
            <div className="space-y-2">
              {jobsData.jobs.map((job) => (
                <div
                  key={job.job_id}
                  className="border border-gray-200 rounded-md p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/jobs/${job.job_id}`)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {job.goal}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {job.website_url}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        job.status === 'completed' ? 'bg-green-100 text-green-800' :
                        job.status === 'running' ? 'bg-blue-100 text-blue-800' :
                        job.status === 'run_failed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status}
                      </span>
                      {hasEvaluation && job.evaluation_status && (
                        <EvaluationStatusBadge status={job.evaluation_status} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
